import React from "react"
import { Button } from "@mimber/ui-web"
import { DownloadOutlined } from "@mimber/ui-web"
import { downloadCsv } from "@/utils"
import type { TableExportButtonProps } from "./TableExportButton.types"

/**
 * A self-contained CSV export button for Ant Design tables.
 *
 * Drop it above any table and supply three things:
 *   - `data` — the currently loaded page data (passed directly from your query)
 *   - `headers` — column header labels
 *   - `toRow` — a transform function from a single row item to string cells
 *
 * TypeScript infers `T` from `data`, so no explicit type parameter is needed.
 *
 * @example
 * <TableExportButton
 *   data={response?.data}
 *   headers={["Invoice #", "Member", "Amount", "Status"]}
 *   toRow={(invoice) => [invoice.invoiceNumber, invoice.memberName ?? "", ...]}
 *   filename="invoices.csv"
 * />
 */
export function TableExportButton<T>({
  data,
  headers,
  toRow,
  filename,
  size = "small",
}: TableExportButtonProps<T>): React.ReactElement {
  const handleClick = () => {
    downloadCsv(headers, (data ?? []).map(toRow), filename)
  }

  return (
    <Button
      icon={<DownloadOutlined />}
      size={size}
      disabled={!data?.length}
      onClick={handleClick}
    >
      Export table
    </Button>
  )
}
